(function attachJudesProbierAB(root, factory) {
  const api = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.JudesProbierAB = api;
  if (root.document) api.autoInit();
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesProbierAB(root) {
  'use strict';

  const EXPERIMENT_ID = 'probier_pdp_ab_20260910';
  // Reporting boundary: 2026-09-24 10:00:00 UTC (12:00 Europe/Berlin).
  const REPORTING_ID = 'probier_pdp_ab_20260924_offer_top';
  const COOKIE_NAME = 'judes_probier_ab';
  const COOKIE_SECONDS = 30 * 24 * 60 * 60;
  const ENTRY_VIEW = 'probier-ab-entry';
  const EVENTS = Object.freeze({
    pageView: 'probier_pdp_view',
    builderStart: 'probier_builder_start',
    summaryView: 'probier_summary_view',
    cartSuccess: 'probier_cart_success',
    checkoutStart: 'probier_checkout_start'
  });
  const JOURNEY_KEY = `${EXPERIMENT_ID}:journey`;
  const JOURNEY_MS = 2 * 60 * 60 * 1000;
  let active = null;
  let journey = null;
  let verifiedCart = false;
  let measurement = null;
  const emitted = new Set();

  function trackingAllowed() {
    try {
      const privacy = root.Shopify?.customerPrivacy || root.Shopify?.trackingConsent;
      if (typeof privacy?.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed() === true;
      return privacy?.userCanBeTracked?.() === true;
    } catch (error) { return false; }
  }

  function storageValue(key) {
    try { return root.localStorage?.getItem(key); } catch (error) { return null; }
  }

  function readJson(value) {
    try { return JSON.parse(value); } catch (error) { return null; }
  }

  function environment() {
    return String(root.Shopify?.theme?.id || '') + ':' + String(root.Shopify?.theme?.role || '');
  }

  function currentJourney() {
    // Experiment attribution is independent of general analytics permission.
    if (!active || Date.now() >= active.expiresAt || (!active.qa && productionArm() !== active.arm)) return null;
    const candidate = journey || readJson(sessionGet(journeyKey()));
    if (!active || !candidate || candidate.experiment !== EXPERIMENT_ID || candidate.arm !== active.arm
      || candidate.qa !== active.qa || candidate.expiresAt !== active.expiresAt || candidate.environment !== environment()
      || !Number.isFinite(candidate.exposedAt) || typeof candidate.id !== 'string'
      || candidate.locale !== active.locale || candidate.country !== active.country
      || Date.now() - candidate.exposedAt > JOURNEY_MS || candidate.exposedAt > Date.now()) return null;
    return candidate;
  }

  function journeyKey() {
    return active?.qa ? `${JOURNEY_KEY}:qa:${active.arm}` : JOURNEY_KEY;
  }

  function recipeForCode(code) {
    return root.JudesTrialBundleRecipes?.recipes.find((recipe) => recipe.discountCodes.standardDiscount === code);
  }

  function validRecipeBasket(payload, recipe) {
    if (!recipe || !Array.isArray(payload?.items) || !payload.items.length) return false;
    const quantities = new Map();
    for (const item of payload.items) {
      const quantity = Number(item.quantity);
      if (!Number.isInteger(quantity) || quantity <= 0) return false;
      if (item.properties?._judes_bundle_type && item.properties._judes_bundle_type !== 'trial') return false;
      if (item.properties?._judes_bundle_recipe && item.properties._judes_bundle_recipe !== recipe.id) return false;
      quantities.set(String(item.id), (quantities.get(String(item.id)) || 0) + quantity);
    }
    for (const item of recipe.fixedItems) {
      if (quantities.get(item.variantId) !== item.quantity) return false;
      quantities.delete(item.variantId);
    }
    for (const group of recipe.choiceGroups) {
      let count = 0;
      for (const id of group.allowedVariantIds) {
        count += quantities.get(id) || 0;
        quantities.delete(id);
      }
      if (count !== group.quantity) return false;
    }
    return quantities.size === 0;
  }

  function legacyBasket() {
    const merge = (itemsKey, qtyKey) => {
      const items = readJson(storageValue(itemsKey));
      const quantities = readJson(storageValue(qtyKey));
      if (!Array.isArray(items) || !Array.isArray(quantities)) return null;
      return items.map((item) => ({ ...item, ...quantities.find((qty) => String(qty.id) === String(item.id)) }));
    };
    const basics = merge('product-basics-bundle', 'product-basic-quantity-bundle');
    const colors = merge('product-color-bundle', 'product-quantity-bundle');
    return basics && colors ? { items: basics.concat(colors) } : null;
  }

  function basketKey(payload) {
    return JSON.stringify((payload?.items || []).map((item) => [String(item.id), Number(item.quantity), Number(item.price || 0)])
      .sort((a, b) => a[0].localeCompare(b[0])));
  }

  function parentInventory(parentVariantId) {
    // These are the actual legacy parent metafield inventories rendered by product-content-custom.
    // They include unselected alternatives and optional basics, not just the new Builder defaults.
    const colors = readJson(root.document.querySelector?.(`.value-${parentVariantId}`)?.value);
    const basics = readJson(root.document.querySelector?.(`.basic-${parentVariantId}`)?.value);
    if (!Array.isArray(colors) || !Array.isArray(basics)) return null;
    const inventory = {};
    for (const item of [...basics, ...colors]) {
      const id = String(item.id);
      const price = Number(item.price);
      if (!/^\d+$/.test(id) || !Number.isInteger(price) || price < 0) return null;
      if (inventory[id] !== undefined && inventory[id] !== price) return null;
      inventory[id] = price;
    }
    return Object.keys(inventory).length ? inventory : null;
  }

  function legacyContextValid(candidate, payload) {
    const recipe = recipeForCode(candidate?.code);
    if (!candidate || !recipe || candidate.parentVariantId !== recipe.parentVariantId
      || candidate.code !== storageValue('discount-code')
      || !candidate.storageSource || candidate.storageSource !== storageValue('lastPartURL')
      || !candidate.inventory || !Array.isArray(payload?.items) || !payload.items.length) return false;
    let selected = 0;
    for (const item of payload.items) {
      const quantity = Number(item.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 0
        || !Object.prototype.hasOwnProperty.call(candidate.inventory, String(item.id))
        || candidate.inventory[String(item.id)] !== Number(item.price)
        || (item.properties?._judes_bundle_type && item.properties._judes_bundle_type !== 'trial')
        || (item.properties?._judes_bundle_recipe && item.properties._judes_bundle_recipe !== recipe.id)) return false;
      selected += quantity;
    }
    return selected > 0;
  }

  function refreshLegacyBasket(candidate, payload) {
    if (!legacyContextValid(candidate, payload)) return false;
    // Selection is mutable within the original parent's inventory; provenance is not.
    candidate.basket = basketKey(payload);
    journey = candidate;
    sessionSet(journeyKey(), JSON.stringify(candidate));
    return true;
  }

  function bindLegacyLink(link, stage) {
    const candidate = currentJourney();
    const payload = legacyBasket();
    const code = storageValue('discount-code');
    const recipe = recipeForCode(code);
    if (!candidate || !recipe) return false;
    if (active.stage === 'product') {
      const parentId = new URL(root.location.href).searchParams.get('variant') || active.parentVariantId;
      if (String(parentId) !== recipe.parentVariantId) return false;
      const storageSource = storageValue('lastPartURL');
      const sourceHandle = String(storageSource || '').split(/[?#]/)[0];
      if (sourceHandle !== new URL(active.productUrl).pathname.split('/').pop()) return false;
      const inventory = parentInventory(parentId);
      if (!inventory) return false;
      candidate.code = code;
      candidate.parentVariantId = recipe.parentVariantId;
      candidate.storageSource = storageSource;
      candidate.inventory = inventory;
    }
    if (!refreshLegacyBasket(candidate, payload)) return false;
    candidate.stages = Array.from(new Set([...(candidate.stages || []), stage]));
    sessionSet(journeyKey(), JSON.stringify(candidate));
    const url = new URL(link.href, root.location.href);
    url.searchParams.set('judes_ab_journey', candidate.id);
    if (active.qa) url.searchParams.set('judes_ab_preview', '1');
    link.href = url.toString();
    return true;
  }

  function cartVerified(payload, result) {
    verifiedCart = Boolean(result?.verified && currentJourney() && decorateCartPayload(payload)?.items?.every((item) => item.properties?._judes_experiment_arm === active.arm));
    return verifiedCart;
  }


  function selectArm(randomValue) {
    return Number(randomValue) < 0.5 ? 'control' : 'variant';
  }

  function cookieText(arm, assignedAt = Date.now()) {
    const value = encodeURIComponent(`${EXPERIMENT_ID}:${arm}:${assignedAt + COOKIE_SECONDS * 1000}`);
    return `${COOKIE_NAME}=${value}; Max-Age=${COOKIE_SECONDS}; Path=/; SameSite=Lax; Secure`;
  }

  function readCookie(cookieSource) {
    const match = String(cookieSource || '')
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${COOKIE_NAME}=`));
    if (!match) return '';
    let value;
    try { value = decodeURIComponent(match.slice(COOKIE_NAME.length + 1)); } catch (error) { return ''; }
    const parts = value.split(':');
    const [experiment, arm, expiryText] = parts;
    const expiry = Number(expiryText);
    const now = Date.now();
    return parts.length === 3 && experiment === EXPERIMENT_ID && ['control', 'variant'].includes(arm)
      && /^\d+$/.test(expiryText) && Number.isSafeInteger(expiry)
      && expiry > now && expiry <= now + COOKIE_SECONDS * 1000 ? arm : '';
  }

  function productionArm() {
    try { return readCookie(root.document.cookie); } catch (error) { return ''; }
  }

  function targetUrl(currentUrl, arm, controlView, variantView) {
    const url = new URL(currentUrl);
    if (url.searchParams.get('judes_ab_qa') === '1') url.searchParams.set('judes_ab_preview', '1');
    url.searchParams.delete('judes_ab_force');
    url.searchParams.delete('judes_ab_qa');
    url.searchParams.delete('judes_ab_unstored');
    url.searchParams.set('view', arm === 'variant' ? variantView : controlView);
    url.searchParams.set('judes_ab', arm);
    return url.toString();
  }

  function entryUrl(currentUrl) {
    const url = new URL(currentUrl);
    url.searchParams.delete('judes_ab');
    url.searchParams.set('view', ENTRY_VIEW);
    return url.toString();
  }

  function isQa(locationValue, shopify, navigatorValue) {
    const params = new URLSearchParams((locationValue && locationValue.search) || '');
    const role = shopify && shopify.theme && String(shopify.theme.role || '').toLowerCase();
    return params.get('judes_ab_qa') === '1'
      || params.get('judes_ab_preview') === '1'
      || params.has('preview_theme_id')
      || Boolean(shopify && shopify.designMode)
      || Boolean(role && role !== 'main')
      || Boolean(navigatorValue && navigatorValue.webdriver);
  }

  function isBot(userAgent) {
    return /bot|crawler|spider|slurp|facebookexternalhit|preview/i.test(String(userAgent || ''));
  }

  function stripAttribution(payload) {
    if (!Array.isArray(payload?.items) || !payload.items.some((item) =>
      Object.keys(item.properties || {}).some((key) => key.startsWith('_judes_experiment')))) return payload;
    return { ...payload, items: payload.items.map((item) => ({ ...item,
      properties: Object.fromEntries(Object.entries(item.properties || {}).filter(([key]) => !key.startsWith('_judes_experiment')))
    })) };
  }

  function decorateCartPayload(payload, explicitArm) {
    const candidate = currentJourney();
    if (!candidate || (explicitArm && explicitArm !== active.arm)) return stripAttribution(payload);
    let recipe;
    if (active.arm === 'control') {
      if (!refreshLegacyBasket(candidate, payload)) return stripAttribution(payload);
      recipe = recipeForCode(candidate.code);
    } else {
      recipe = root.JudesTrialBundleRecipes?.recipes.find((item) => item.id === payload?.items?.[0]?.properties?._judes_bundle_recipe);
    }
    if (active.arm === 'variant' && !validRecipeBasket(payload, recipe)) return stripAttribution(payload);
    return { ...payload, items: payload.items.map((item) => ({ ...item, properties: {
      ...(item.properties || {}), _judes_experiment: REPORTING_ID,
      _judes_experiment_arm: active.arm, _judes_experiment_qa: active.qa ? '1' : '0'
    } })) };
  }

  function randomValue() {
    if (root.crypto && typeof root.crypto.getRandomValues === 'function') {
      const values = new Uint32Array(1);
      root.crypto.getRandomValues(values);
      return values[0] / 4294967296;
    }
    return Math.random();
  }

  function sessionGet(key) {
    try {
      return root.sessionStorage && root.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      if (root.sessionStorage) root.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage refusal must not block shopping.
    }
  }

  function writeAssignment(arm, qa) {
    if (qa) {
      sessionSet(`${EXPERIMENT_ID}:qa-arm`, arm);
      return;
    }
    try { root.document.cookie = cookieText(arm); } catch (error) { /* Shopping remains available. */ }
  }

  function assignedArm(qa, forcedArm) {
    if (qa && ['control', 'variant'].includes(forcedArm)) return forcedArm;
    const cookieArm = !qa && productionArm();
    if (cookieArm) return cookieArm;
    const qaArm = qa && sessionGet(`${EXPERIMENT_ID}:qa-arm`);
    if (['control', 'variant'].includes(qaArm)) return qaArm;
    return selectArm(randomValue());
  }

  function commonPayload(detail) {
    return {
      experiment_id: REPORTING_ID,
      experiment_arm: active.arm,
      product_handle: 'probier-set',
      storefront_locale: active.locale,
      storefront_country: active.country,
      experiment_stage: active.stage,
      ...(detail || {})
    };
  }

  function track(eventName, detail, onceKey) {
    if (!currentJourney() || !eventName) return false;
    const milestone = { [EVENTS.pageView]: 'exposure', [EVENTS.builderStart]: 'builder_start',
      [EVENTS.summaryView]: 'summary', [EVENTS.cartSuccess]: 'cart_success', [EVENTS.checkoutStart]: 'checkout_start' }[eventName];
    if (milestone) measurement?.track(milestone);
    // General gtag retains the real Shopify consent gate, with separate dedup.
    const analyticsKey = `${REPORTING_ID}:gtag:${active.arm}:${active.expiresAt}:${onceKey || eventName}`;
    const payload = commonPayload(detail);
    if (!active.qa && trackingAllowed() && !emitted.has(analyticsKey) && !sessionGet(analyticsKey)) {
      emitted.add(analyticsKey); sessionSet(analyticsKey, '1');
      root.dataLayer = root.dataLayer || [];
      root.gtag = root.gtag || function gtag() { root.dataLayer.push(arguments); };
      root.gtag('event', eventName, payload);
    }
    const storageKey = `${REPORTING_ID}:${active.qa ? 'qa' : 'production'}:${active.arm}:${onceKey || eventName}`;
    if (emitted.has(storageKey) || sessionGet(storageKey)) return false;
    emitted.add(storageKey);
    sessionSet(storageKey, '1');

    root.__judesProbierABEvents = root.__judesProbierABEvents || [];
    root.__judesProbierABEvents.push({ event: eventName, qa: active.qa, ...payload });
    return true;
  }

  function trackPageView(detail) {
    return track(EVENTS.pageView, detail, EVENTS.pageView);
  }

  function trackBuilderStart(detail) {
    return track(EVENTS.builderStart, detail, EVENTS.builderStart);
  }

  function trackSummaryView(detail) {
    return track(EVENTS.summaryView, detail, EVENTS.summaryView);
  }

  function trackCartSuccess(detail) {
    if (!verifiedCart) return false;
    return track(EVENTS.cartSuccess, detail, EVENTS.cartSuccess);
  }

  function trackCheckoutStart(detail) {
    if (!verifiedCart) return false;
    return track(EVENTS.checkoutStart, detail, EVENTS.checkoutStart);
  }

  function activateRouter(element) {
    const qa = isQa(root.location, root.Shopify, root.navigator);
    const params = new URLSearchParams(root.location.search || '');
    const forcedArm = params.get('judes_ab_force');

    if (isBot(root.navigator && root.navigator.userAgent) && !qa) return;

    const arm = assignedArm(qa, forcedArm);
    // Existing assignments retain their original expiry; only entry can create one.
    if (qa || !productionArm()) writeAssignment(arm, qa);
    const target = targetUrl(
      root.location.href,
      arm,
      element.dataset.controlView,
      element.dataset.variantView
    );
    const routed = new URL(target);
    if (qa) routed.searchParams.set('judes_ab_preview', '1');
    else if (!productionArm()) routed.searchParams.set('judes_ab_unstored', '1');
    root.document.documentElement.classList.add('judes-probier-ab-routing');
    root.location.replace(routed.toString());
  }

  function activateTracker(element) {
    const qa = isQa(root.location, root.Shopify, root.navigator);
    const configuredArm = element.dataset.arm;
    if (isBot(root.navigator && root.navigator.userAgent) && !qa) return;
    if (!['control', 'variant'].includes(configuredArm)) return;

    if (!qa) {
      const cookieArm = productionArm();
      if (cookieArm !== configuredArm) {
        if ((element.dataset.stage || 'product') === 'product') {
          // Do not loop when the browser refuses cookies. Render the existing arm untracked.
          if (cookieArm || new URL(root.location.href).searchParams.get('judes_ab_unstored') !== '1') root.location.replace(entryUrl(root.location.href));
        }
        return;
      }
    }

    const arm = configuredArm;
    let expiresAt;
    if (qa) {
      const key = `${EXPERIMENT_ID}:qa-expiry:${arm}`;
      expiresAt = Number(storageValue(key));
      if (!Number.isSafeInteger(expiresAt) || expiresAt <= Date.now() || expiresAt > Date.now() + COOKIE_SECONDS * 1000) {
        expiresAt = Date.now() + COOKIE_SECONDS * 1000;
        try { root.localStorage?.setItem(key, String(expiresAt)); } catch (_) { /* QA can remain in memory. */ }
      }
    } else {
      const part = String(root.document.cookie).split(';').map(value => value.trim()).find(value => value.startsWith(`${COOKIE_NAME}=`));
      expiresAt = Number(decodeURIComponent(part.slice(COOKIE_NAME.length + 1)).split(':')[2]);
    }
    active = {
      expiresAt,
      arm,
      qa,
      locale: element.dataset.locale || root.document.documentElement.lang || 'en',
      country: element.dataset.country || '',
      stage: element.dataset.stage || 'product',
      parentVariantId: element.dataset.parentVariantId || '',
      productUrl: root.location.href
    };
    if (active.stage === 'product') {
      if (!element.dataset.productId || element.dataset.productId !== element.dataset.probierProductId) { active = null; return; }
      journey = {
        experiment: EXPERIMENT_ID, id: `${Date.now()}-${randomValue().toString(36).slice(2)}`,
        arm, qa, expiresAt, environment: environment(), locale: active.locale, country: active.country, exposedAt: Date.now()
      };
      sessionSet(journeyKey(), JSON.stringify(journey));
    } else {
      const candidate = currentJourney();
      const token = new URL(root.location.href).searchParams.get('judes_ab_journey');
      if (!candidate || token !== candidate.id || !candidate.stages?.includes(active.stage)
        || !refreshLegacyBasket(candidate, legacyBasket())) { active = null; return; }
      journey = candidate;
    }
    if (!measurement) measurement = root.JudesProbierMeasurement?.create({ endpoint: element.dataset.measurementEndpoint, arm, qa, expiresAt }) || null;
    const { productUrl, ...publicState } = active;
    root.__judesProbierABState = { experimentId: EXPERIMENT_ID, reportingId: REPORTING_ID, ...publicState };

    if (active.stage === 'product') trackPageView();
    if (active.stage === 'builder') trackBuilderStart();
    if (active.stage === 'summary') trackSummaryView();

    if (active.stage === 'builder' && arm === 'control') {
      root.document.addEventListener('click', (event) => {
        const link = event.target?.closest?.('a[href*="/pages/bundle-basics"]');
        if (link) bindLegacyLink(link, 'summary');
      });
    }
    if (active.stage === 'product' && arm === 'control') {
      const exposureUrl = new URL(root.location.href);
      root.document.addEventListener('change', (event) => {
        if (!event.target?.closest?.('variant-selects, variant-radios')) return;
        const current = new URL(root.location.href);
        exposureUrl.searchParams.forEach((value, key) => {
          if (key !== 'variant') current.searchParams.set(key, value);
        });
        current.hash = exposureUrl.hash;
        root.history.replaceState(root.history.state, '', current.toString());
      });
      root.document.addEventListener('click', (event) => {
        const link = event.target && event.target.closest && event.target.closest('a[href*="/pages/bundle-product"]');
        if (link && bindLegacyLink(link, 'builder')) trackBuilderStart();
      });
    }
  }

  function scan() {
    root.document.querySelectorAll('[data-judes-probier-ab]').forEach((element) => {
      if (element.dataset.probierAbReady === 'true') return;
      element.dataset.probierAbReady = 'true';
      if (element.dataset.mode === 'router') activateRouter(element);
      else activateTracker(element);
    });
  }

  function autoInit() {
    // Assignment and routing never wait for analytics permission or its API loader.
    const rescanTrackers = () => {
      if (currentJourney()) {
        if (active.stage === 'product') trackPageView();
        if (active.stage === 'builder') trackBuilderStart();
        if (active.stage === 'summary') trackSummaryView();
        return;
      }
      root.document.querySelectorAll('[data-judes-probier-ab]').forEach((element) => {
        if (element.dataset.mode !== 'router') element.dataset.probierAbReady = 'false';
      });
      scan();
    };
    scan();
    if (!isQa(root.location, root.Shopify, root.navigator)
      && !root.Shopify?.customerPrivacy && !root.Shopify?.trackingConsent
      && typeof root.Shopify?.loadFeatures === 'function') {
      root.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], rescanTrackers);
    }
    root.document.addEventListener('visitorConsentCollected', () => {
      if (isQa(root.location, root.Shopify, root.navigator)) return;
      // Consent never resets experiment journey, identifier, assignment or expiry.
      if (active) {
        if (active.stage === 'product') trackPageView();
        if (active.stage === 'builder') trackBuilderStart();
        if (active.stage === 'summary') trackSummaryView();
      } else rescanTrackers();
    });
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', scan, { once: true });
    }
  }

  return Object.freeze({
    EXPERIMENT_ID,
    REPORTING_ID,
    EVENTS,
    selectArm,
    cookieText,
    readCookie,
    targetUrl,
    entryUrl,
    isQa,
    isBot,
    decorateCartPayload,
    cartVerified,
    recipeForCode,
    validRecipeBasket,
    trackPageView,
    trackBuilderStart,
    trackSummaryView,
    trackCartSuccess,
    trackCheckoutStart,
    autoInit
  });
}));
